import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, XIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { logActivity, ActivityTypes } from '../utils/activityLogger'
import { PropertyAddressFields } from '../components/property/PropertyAddressFields'
import { PropertyInfoFields } from '../components/property/PropertyInfoFields'
import { PropertyPaymentFields } from '../components/property/PropertyPaymentFields'
import { PropertyAssignmentFields } from '../components/property/PropertyAssignmentFields'
import { PropertyContactsFields } from '../components/property/PropertyContactsFields'
import { PropertyNotesFields } from '../components/property/PropertyNotesFields'
import type { AdditionalAccess, PropertyNote, CustomerPaymentDefaults } from '../components/property/types'
import type { CustomerContact } from '../components/customer/types'

export default function PropertyCreate() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState<Array<{ id: string; company_name: string }>>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'address' | 'info' | 'payment' | 'contacts' | 'notes'>('address')

  // Property Information
  const [propertyData, setPropertyData] = useState({
    customer_id: '',
    name: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    building_type: '',
    units: '',
    stories: '',
    access_type: '',
    access_info: '',
    payment_method: '',
    sales_tax_status: '',
    default_assigned_crew_id: '',
    default_assigned_employee_id: '',
  })

  const [additionalAccess, setAdditionalAccess] = useState<AdditionalAccess[]>([])
  const [propertyNotes, setPropertyNotes] = useState<PropertyNote[]>([])
  const [customerContacts, setCustomerContacts] = useState<CustomerContact[]>([])
  const [customerDefaults, setCustomerDefaults] = useState<CustomerPaymentDefaults | null>(null)
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set())
  const [manualLat, setManualLat] = useState('')
  const [manualLng, setManualLng] = useState('')
  const [geocodePreview, setGeocodePreview] = useState<{
    latitude: number
    longitude: number
    matched_address?: string | null
  } | null>(null)
  const [coordsRequired, setCoordsRequired] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  const handleAddContactToCustomer = async (contact: CustomerContact) => {
    if (!propertyData.customer_id) {
      alert('Please select a customer first')
      return
    }
    try {
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select('contacts')
        .eq('id', propertyData.customer_id)
        .single()

      if (fetchError) throw fetchError

      const name = `${contact.first_name} ${contact.last_name}`.trim()
      const newContactWithId = {
        ...contact,
        name,
        title: contact.title || null,
        phone: contact.phone || null,
        cell: contact.cell || null,
        email: contact.email || null,
      }

      const currentContacts =
        customer?.contacts && Array.isArray(customer.contacts) ? customer.contacts : []
      const updatedContacts = [...currentContacts, newContactWithId]

      const { error: updateError } = await supabase
        .from('customers')
        .update({ contacts: updatedContacts })
        .eq('id', propertyData.customer_id)

      if (updateError) throw updateError
      await fetchCustomerContacts(propertyData.customer_id)
    } catch (error) {
      console.error('Error creating contact:', error)
      alert('Error creating contact: ' + (error as any).message)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [])

  useEffect(() => {
    if (propertyData.customer_id) {
      fetchCustomerContacts(propertyData.customer_id)
      fetchCustomerDefaults(propertyData.customer_id)
    } else {
      setCustomerContacts([])
      setCustomerDefaults(null)
    }
  }, [propertyData.customer_id])

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name')
        .order('company_name')
      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
      alert('Error loading customers: ' + (error as any).message)
    }
  }

  const fetchCustomerContacts = async (customerId: string) => {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('contacts')
        .eq('id', customerId)
        .single()

      if (error) throw error

      if (customer && customer.contacts && Array.isArray(customer.contacts)) {
        setCustomerContacts(customer.contacts)
      } else {
        setCustomerContacts([])
      }
    } catch (error) {
      console.error('Error fetching customer contacts:', error)
      setCustomerContacts([])
    }
  }

  const fetchCustomerDefaults = async (customerId: string) => {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('payment_method, sales_tax_status')
        .eq('id', customerId)
        .single()

      if (error) throw error

      setCustomerDefaults({
        payment_method: customer?.payment_method || null,
        sales_tax_status: customer?.sales_tax_status || null,
      })
    } catch (error) {
      console.error('Error fetching customer defaults:', error)
      setCustomerDefaults(null)
    }
  }

  const validateTab = (tab: 'address' | 'info' | 'payment' | 'contacts' | 'notes'): boolean => {
    switch (tab) {
      case 'address':
        return !!(propertyData.customer_id && propertyData.name && propertyData.address)
      case 'info':
        return !!propertyData.building_type
      case 'payment':
        return true // Customer Default is valid; overrides are optional
      case 'contacts':
        return true // Contacts are optional
      case 'notes':
        return true // Notes are optional
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!validateTab(activeTab)) {
      alert('Please fill in all required fields before continuing')
      return
    }

    const tabs: Array<'address' | 'info' | 'payment' | 'contacts' | 'notes'> = [
      'address',
      'info',
      'payment',
      'contacts',
      'notes',
    ]
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const tabs: Array<'address' | 'info' | 'payment' | 'contacts' | 'notes'> = [
      'address',
      'info',
      'payment',
      'contacts',
      'notes',
    ]
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1])
    }
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await handleSubmit({ preventDefault: () => {} } as any)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!propertyData.customer_id) {
        alert('Please select a customer')
        setLoading(false)
        return
      }

      if (!propertyData.name) {
        alert('Property name is required')
        setLoading(false)
        return
      }
      if (!propertyData.address?.trim()) {
        alert('Street address is required so we can set map coordinates')
        setLoading(false)
        return
      }

      const { geocodeAddress, savePropertyCoordinates } = await import('../lib/geocodeProperty')
      let coords = geocodePreview
      if (!coords) {
        coords = await geocodeAddress({
          address: propertyData.address,
          city: propertyData.city,
          state: propertyData.state,
          zip_code: propertyData.zip,
        })
      }
      if (!coords) {
        const lat = Number(manualLat)
        const lng = Number(manualLng)
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          coords = { latitude: lat, longitude: lng, matched_address: 'Manual entry' }
        } else {
          setCoordsRequired(true)
          alert(
            "Couldn't find exact coordinates for this address. Fix the address or enter latitude/longitude manually, then save again."
          )
          setLoading(false)
          return
        }
      } else {
        setGeocodePreview(coords)
      }

      // Prepare additional access data
      const additionalAccessData = additionalAccess
        .filter((a) => a.location && a.access_type && a.access_info)
        .map((a) => ({
          location: a.location,
          access_type: a.access_type,
          access_info: a.access_info,
        }))

      // Build insert object conditionally to avoid errors if column doesn't exist yet
      const insertData: any = {
        customer_id: propertyData.customer_id,
        name: propertyData.name,
        address: propertyData.address || null,
        city: propertyData.city || null,
        state: propertyData.state || null,
        zip_code: propertyData.zip || null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        geocode_source: coords.matched_address === 'Manual entry' ? 'manual' : 'geocode',
        geocode_accuracy: coords.matched_address === 'Manual entry' ? 'manual' : 'rooftop',
        geocode_matched_address: coords.matched_address || null,
        building_type: propertyData.building_type || null,
        units: propertyData.units || null,
        stories: propertyData.stories || null,
        access_type: propertyData.access_type || null,
        access_info: propertyData.access_info || null,
        additional_access: additionalAccessData.length > 0 ? additionalAccessData : null,
        payment_method: propertyData.payment_method || null,
        sales_tax_status: propertyData.sales_tax_status || null,
        default_assigned_crew_id: propertyData.default_assigned_crew_id || null,
        default_assigned_employee_id: propertyData.default_assigned_employee_id || null,
        notes: propertyNotes.length > 0 ? propertyNotes : [],
      }

      // Only include address_line_2 if it has a value (column may not exist in DB yet)
      if (propertyData.address_line_2) {
        insertData.address_line_2 = propertyData.address_line_2
      }

      // Include selected contacts
      if (selectedContactIds.size > 0 && customerContacts.length > 0) {
        const selectedContacts = customerContacts.filter((c) => selectedContactIds.has(c.id))
        insertData.contacts = selectedContacts
      } else {
        insertData.contacts = []
      }

      // Create property
      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .insert([insertData])
        .select()
        .single()

      if (propertyError) throw propertyError

      // Log activity
      await logActivity({
        activity_type: ActivityTypes.PROPERTY_CREATED,
        entity_type: 'property',
        entity_id: property.id,
        description: `Property "${propertyData.name}" created`,
        metadata: {
          customer_id: propertyData.customer_id,
          address: propertyData.address,
          city: propertyData.city,
          state: propertyData.state,
        },
      })

      setNotification({
        type: 'success',
        message: 'Property created successfully!',
      })
      setTimeout(() => {
        setNotification(null)
        navigate('/properties')
      }, 2000)
    } catch (error) {
      console.error('Error creating property:', error)
      setNotification({
        type: 'error',
        message: 'Error creating property: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <MobilePage className="max-w-4xl mx-auto pb-28 lg:pb-8">
        <MobilePageHeader
          sticky
          title="Create Property"
          subtitle="Add a new property location"
          onBack={() => navigate('/properties')}
          backLabel="Back to Properties"
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Navigation */}
          <div className={mobile.surface}>
            <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <div
                className={`px-4 sm:px-6 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'address'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'info' || activeTab === 'payment' || activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Address
              </div>
              <div
                className={`px-4 sm:px-6 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'info'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'payment' || activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Info
              </div>
              <div
                className={`px-4 sm:px-6 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'payment'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'contacts' || activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Payment and Sales Tax
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('contacts')}
                className={`px-4 sm:px-6 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium transition-colors ${
                  activeTab === 'contacts'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'notes'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Contacts
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('notes')}
                className={`px-4 sm:px-6 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium transition-colors ${
                  activeTab === 'notes'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Notes and Activity
              </button>
            </div>
            <div className="p-4 sm:p-6">
              {activeTab === 'address' && (
                <>
                  {(coordsRequired || geocodePreview) && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3 mb-4">
                      {geocodePreview ? (
                        <div className="text-sm text-amber-900">
                          <p className="font-semibold">Coordinates found</p>
                          <p className="mt-1">{geocodePreview.matched_address || 'Matched address'}</p>
                          <p className="text-xs mt-1">
                            {geocodePreview.latitude.toFixed(6)}, {geocodePreview.longitude.toFixed(6)}{' '}
                            <a
                              className="text-blue-700 underline ml-2"
                              href={`https://www.google.com/maps?q=${geocodePreview.latitude},${geocodePreview.longitude}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open in Google Maps
                            </a>
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-amber-900 font-medium">
                          Exact coordinates are required. Enter them manually if the address cannot be matched.
                        </p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="text-sm">
                          Latitude
                          <input
                            className={`mt-1 ${mobile.field}`}
                            value={manualLat}
                            onChange={(e) => setManualLat(e.target.value)}
                            placeholder="40.812345"
                          />
                        </label>
                        <label className="text-sm">
                          Longitude
                          <input
                            className={`mt-1 ${mobile.field}`}
                            value={manualLng}
                            onChange={(e) => setManualLng(e.target.value)}
                            placeholder="-73.912345"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                  <PropertyAddressFields
                    value={propertyData}
                    onChange={(v) => setPropertyData({ ...propertyData, ...v })}
                    customers={customers}
                  />
                </>
              )}

              {activeTab === 'info' && (
                <div className="space-y-8">
                  <PropertyInfoFields
                    value={{
                      building_type: propertyData.building_type,
                      units: propertyData.units,
                      stories: propertyData.stories,
                      access_type: propertyData.access_type,
                      access_info: propertyData.access_info,
                    }}
                    onChange={(info) => setPropertyData({ ...propertyData, ...info })}
                    additionalAccess={additionalAccess}
                    onAdditionalAccessChange={setAdditionalAccess}
                  />
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Default Assignment</h3>
                    <PropertyAssignmentFields
                      value={{
                        default_assigned_crew_id: propertyData.default_assigned_crew_id,
                        default_assigned_employee_id: propertyData.default_assigned_employee_id,
                      }}
                      onChange={(assignment) =>
                        setPropertyData({ ...propertyData, ...assignment })
                      }
                    />
                  </div>
                </div>
              )}

              {activeTab === 'payment' && (
                <PropertyPaymentFields
                  value={{
                    payment_method: propertyData.payment_method,
                    sales_tax_status: propertyData.sales_tax_status,
                  }}
                  onChange={(payment) => setPropertyData({ ...propertyData, ...payment })}
                  customerDefaults={customerDefaults}
                />
              )}

              {activeTab === 'contacts' && (
                <PropertyContactsFields
                  customerContacts={customerContacts}
                  selectedContactIds={selectedContactIds}
                  onSelectedContactIdsChange={setSelectedContactIds}
                  onAddContact={handleAddContactToCustomer}
                />
              )}

              {activeTab === 'notes' && (
                <PropertyNotesFields
                  propertyNotes={propertyNotes}
                  onPropertyNotesChange={setPropertyNotes}
                />
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-4xl mx-auto lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/properties')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>

            <div className="flex gap-2 sm:gap-3">
              {activeTab !== 'address' && (
                <button
                  type="button"
                  onClick={handleBack}
                  className={mobile.secondaryBtn}
                >
                  Back
                </button>
              )}
              {activeTab === 'notes' ? (
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={loading}
                  className={mobile.primaryBtn}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4" />
                      <span className="sm:hidden">Create</span>
                      <span className="hidden sm:inline">Create Property</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!validateTab(activeTab)}
                  className={mobile.primaryBtn}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </form>

      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className={`text-lg font-medium ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
